import React, { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { Page } from 'pages'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { gender, queryClient as qc, statuses, visibleTypes } from 'keys'
import { getAssessmentDefFetch, getAssessmentFetch, updateAssessmentStatusFetch, uploadAudioFileFetch } from 'store/middlewares/assessment.crud'
import PerformAssessment from './PerformAssessment'
import MicRecorder from 'mic-recorder-to-mp3';
import ReactPlayer from 'react-player';
import { useSelector } from 'react-redux'
import { screen_4_narrative_text_read_aloud_female, screen_4_word_list_read_aloud_female, screen_4_word_list_read_aloud_male, screen_4_narrative_text_read_aloud_male } from 'record1'
import { changeItem } from 'utils/array'

const Mp3Recorder = new MicRecorder({ bitRate: 128 });

const AssessmentRecordFooter = () => {
    const params = useParams()
    const queryClient = useQueryClient()
    const assessmentId = params.assessmentID
    const [assessments, setAssessments] = useState()
    const [readAloudFlag, setReadAloudFlag] = useState(false)
    const [assessment, setAssessment] = useState()
    const [currentStep, setCurrentStep] = useState(0)
    const [audioChunks, setAudioChunks] = useState([])
    const [micDisabled, setMicDisabled] = useState(false)
    const [audioURL, setAudioURL] = useState('')
    const [isRecording, setIsRecording] = useState(false)
    const [finishFlag, setFinishFlag] = useState(false)
    const [tooltipOpen, setTooltipOpen] = useState(true)
    const [blobURL, setBlobURL] = useState('')
    const [assessmentDef, setAssessmentDef] = useState(null)
    const { testTaker = {} } = useSelector(state => state.testTaker)
    const isMaleGender = testTaker.gender == gender.male

    const canvasRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const animationIdRef = useRef(null);

    const now = new Date().getTime();
    const testTakersExpiration = JSON.parse(localStorage.getItem("children-process-expired"))
    const sessionExpired = (testTakersExpiration?.[testTaker.test_taker_id] && now > parseInt(testTakersExpiration?.[testTaker.test_taker_id]))
        || !testTakersExpiration?.[testTaker.test_taker_id]

    const assessmentDefData = useQuery({
        queryKey: [qc.assessmentDef],
        queryFn: async ({ signal }) => {
            const { data } = await getAssessmentDefFetch({ id: assessment?.reading_assessment_def_id, signal, })
            setAssessmentDef(data)
            return data
        },
        enabled: !!assessment?.reading_assessment_def_id
    })

    const assessmentData = useQuery({
        queryKey: [qc.performAssessment],
        queryFn: async ({ signal }) => {
            const { data } = await getAssessmentFetch({ id: assessmentId, signal, })
            setAssessments([data])
            setAssessment(data)
            return data
        }
    })

    const { mutateAsync: uploadFile } = useMutation({
        mutationFn: uploadAudioFileFetch
    })

    const { mutateAsync: updateAssessmentStatus } = useMutation({
        mutationFn: updateAssessmentStatusFetch
    })

    const uploadRecord = useCallback(async (blobURL) => {
        //update assessment status to UPLOADING
        await updateAssessmentStatus({
            status: statuses.uploading,
            sub_status: null,
            id: assessment.id
        })
        const currectAssessmentIndex = assessments.findIndex((ass) => ass.id == assessment.id)
        if (currectAssessmentIndex != -1) {
            const nAssessments = changeItem(assessments, currectAssessmentIndex, { ...assessment, status: statuses.uploading })
            setAssessments(nAssessments)
        }
        // //upload file to S3
        await uploadFile({ id: assessment.id, file: blobURL })
        queryClient.invalidateQueries({ queryKey: [qc.assessments] })
        queryClient.invalidateQueries({ queryKey: [qc.dashboard] })
    }, [assessment, assessments])

    const assessmentDefItem = {
        [visibleTypes.PARAGRAPH]: {
            record: isMaleGender ? screen_4_narrative_text_read_aloud_male : screen_4_narrative_text_read_aloud_female,
        },
        [visibleTypes.WORD_GROUP]: {
            record: isMaleGender ? screen_4_word_list_read_aloud_male : screen_4_word_list_read_aloud_female
        }
    }
    const item = assessmentDefItem?.[assessmentDef?.visible_type]

    const draw = (analyser) => {
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        analyser.getByteFrequencyData(dataArray);

        if (context) {

            context.fillStyle = 'rgba(255, 255, 255, 0.3)';
            context.fillRect(0, 0, canvas.width, canvas.height);

            const barWidth = (canvas.width / bufferLength) * 2.5;
            let barHeight;
            let x = 0;

            for (let i = 0; i < bufferLength; i++) {
                barHeight = dataArray[i] / 6; // הקטנה של גובה הבר
                context.fillStyle = 'gray';
                context.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
                x += barWidth + 1;
            }

            animationIdRef.current = requestAnimationFrame(() => draw(analyser));
        }
    };

    const startRecord = useCallback(() => {
        if (!micDisabled) {
            Mp3Recorder
                .start()
                .then((stream) => {
                    setIsRecording(true)
                    mediaStreamRef.current = stream
                    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)()
                    analyserRef.current = audioContextRef.current.createAnalyser()
                    const source = audioContextRef.current.createMediaStreamSource(stream)
                    source.connect(analyserRef.current)
                    analyserRef.current.fftSize = 2048

                    draw(analyserRef.current)
                }).catch((e) => console.error(e));
        }
    }, [micDisabled])

    const stopRecord = useCallback((successFunc) => {
        Mp3Recorder
            ?.stop()
            ?.getMp3()
            ?.then(([buffer, blob]) => {
                let nAudioChunks = [...audioChunks, blob]
                setAudioChunks(nAudioChunks);
                setIsRecording(false)
                debugger
                const audioBlob = new Blob(nAudioChunks, { type: 'audio/mp3' });
                setBlobURL(audioBlob)
                successFunc?.(audioBlob)
            }).catch((e) => {
                console.log(e)
            });
    }, [assessments, assessment, audioChunks, Mp3Recorder])

    const successFunc = (audioBlob) => {
        uploadRecord(audioBlob)
    }

    const finishRecord = useCallback(() => {
        stopRecord(successFunc)
    }, [assessment, assessments, audioChunks, blobURL])

    useEffect(() => {
        return (() => {
            setTimeout(() => {
                Mp3Recorder?.stop()
            }, 3000);
        })

    }, [Mp3Recorder])

    const commonValue = {
        assessment, setAssessment,
        assessments, setAssessments,
        currentStep, setCurrentStep,
        readAloudFlag, setReadAloudFlag,
        assessment,
        assessmentDef: assessmentDef,
        micDisabled, setMicDisabled,
        audioChunks, setAudioChunks,
        audioURL, setAudioURL,
        Mp3Recorder,
        finishFlag, setFinishFlag,
        isMaleGender: isMaleGender,
        isRecording, setIsRecording,
        tooltipOpen, setTooltipOpen,
        startRecord,
        stopRecord,
        finishRecord,
        sessionExpired,
        canvasRef
    }

    return (
        <Page>
            <PerformAssessmentContext.Provider value={commonValue}>
                <PerformAssessment />
            </PerformAssessmentContext.Provider>
            <ReactPlayer
                playing={readAloudFlag}
                url={item?.record}
                width='0px' height='0px'
                onEnded={startRecord}
            />
        </Page>
    )
}

export const PerformAssessmentContext = createContext()
export default AssessmentRecordFooter